import React from 'react';
import { useSelector } from 'react-redux';
import { Pill } from '../../../ui';
import {
    ActionsLigne, CelluleNom, LienIcone, MonoCell, Muted,
} from '../../../security/security.styled';
import { PaneTable, triAlpha } from '../paneTable';

/* Destination portail par type de groupe.
   Les six libellés sont ceux réellement stockés — la collecte les traduit —, et
   la correspondance est exacte plutôt qu'approchée : l'ancienne version
   cherchait « mail-enabled security » dans un champ qui contient « Groupe de
   sécurité à extension de messagerie », et les 722 groupes concernés
   atterrissaient sur la fiche Entra au lieu du centre d'administration
   Exchange, seul endroit où ils s'administrent. */
const ONGLET_EXCHANGE = {
    "Microsoft 365": 0,
    "Liste de distribution": 2,
    "Groupe de sécurité à extension de messagerie": 3,
    "Liste de distribution dynamique": 7,
};

const TON_TYPE = {
    "Microsoft 365": "accent",
    "Groupe de sécurité dynamique": "rssi",
    "Liste de distribution dynamique": "rssi",
};

// Le rôle du compte dans le groupe. Propriétaire et modérateur sont des
// pouvoirs, membre est un état : seuls les deux premiers sont mis en avant.
const ROLES = {
    member: { libelle: "Membre", ton: "neutral" },
    owner: { libelle: "Propriétaire", ton: "accent" },
    moderator: { libelle: "Modérateur", ton: "warn" },
};

function urlPortail(groupe) {
    const identifiant = groupe.objectId || groupe.id;
    if (!identifiant) return null;
    const onglet = ONGLET_EXCHANGE[groupe.groupTypes];
    return onglet === undefined
        ? `https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${identifiant}/menuId/`
        : `https://admin.exchange.microsoft.com/#/groups/:/GroupDetails/${identifiant}/${onglet}`;
}

function estVrai(valeur) {
    return valeur === true || valeur === "true";
}

const TRI_PAR_NOM = [{ id: 'displayName', desc: false }];

const COLONNES = [
    {
        header: "Groupe",
        accessorKey: "displayName",
        sortingFn: triAlpha,
        cell: ({ row }) => {
            const url = urlPortail(row.original);
            const exchange = ONGLET_EXCHANGE[row.original.groupTypes] !== undefined;
            return (
                <CelluleNom>
                    <span className="nom">{row.original.displayName || <Muted>Sans nom</Muted>}</span>
                    {url && (
                        <ActionsLigne>
                            <LienIcone
                                $mini
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                title={exchange
                                    ? `Administrer ${row.original.displayName} dans Exchange`
                                    : `Objet Entra ID de ${row.original.displayName}`}
                                aria-label={exchange ? "Ouvrir dans Exchange" : "Ouvrir dans Entra ID"}
                            >
                                <i className={exchange ? 'bx bx-envelope' : 'bx bxl-microsoft'} />
                            </LienIcone>
                        </ActionsLigne>
                    )}
                </CelluleNom>
            );
        },
    },
    {
        header: "Type",
        accessorKey: "groupTypes",
        cell: ({ row, getValue }) => (
            <CelluleNom>
                {getValue() ? <Pill $tone={TON_TYPE[getValue()] || 'neutral'}>{getValue()}</Pill> : <Muted>—</Muted>}
                {/* La règle d'appartenance explique pourquoi le compte est là :
                    229 groupes du parc sont peuplés par règle et non à la main. */}
                {row.original.membershipRule && (
                    <i
                        className="bx bx-filter-alt"
                        title={row.original.membershipRule}
                        style={{ cursor: 'help', opacity: 0.55 }}
                    />
                )}
            </CelluleNom>
        ),
    },
    {
        header: "Adresse",
        accessorKey: "mail",
        cell: ({ getValue }) => (getValue() ? <MonoCell>{getValue()}</MonoCell> : <Muted>—</Muted>),
    },
    {
        // Trois colonnes de « true / false » — sécurité, courrier, équipe — plus
        // la visibilité, remplacées par les seules caractéristiques présentes.
        // Ce qui est absent n'a pas besoin d'une case vide pour se dire.
        header: "Caractéristiques",
        id: "caracteristiques",
        enableSorting: false,
        accessorFn: (row) => [
            estVrai(row.securityEnabled) ? "sécurité" : "",
            estVrai(row.mailEnabled) ? "courrier" : "",
            (row.resourceProvisioningOptions || []).includes("Team") ? "teams" : "",
            row.visibility || "",
        ].filter(Boolean).join(" "),
        cell: ({ row }) => {
            const groupe = row.original;
            const equipe = (groupe.resourceProvisioningOptions || []).includes("Team");
            const rien = !estVrai(groupe.securityEnabled) && !estVrai(groupe.mailEnabled)
                && !equipe && !groupe.visibility;
            return (
                <span style={{ display: 'inline-flex', gap: '5px', flexWrap: 'wrap' }}>
                    {estVrai(groupe.securityEnabled) && <Pill $tone="neutral">Sécurité</Pill>}
                    {estVrai(groupe.mailEnabled) && <Pill $tone="neutral">Courrier</Pill>}
                    {equipe && <Pill $tone="accent">Teams</Pill>}
                    {groupe.visibility && (
                        <Pill $tone={groupe.visibility === "Public" ? 'warn' : 'neutral'}>
                            {groupe.visibility === "Public" ? "Public" : "Privé"}
                        </Pill>
                    )}
                    {rien && <Muted>—</Muted>}
                </span>
            );
        },
    },
    {
        header: "Rôle",
        id: "role",
        accessorFn: (row) => (row.accessType || []).join(" "),
        cell: ({ row }) => {
            const acces = Array.isArray(row.original.accessType) ? row.original.accessType : [];
            if (acces.length === 0) return <Muted>—</Muted>;
            return (
                <span style={{ display: 'inline-flex', gap: '5px', flexWrap: 'wrap' }}>
                    {acces.map((role) => (
                        <Pill key={role} $tone={ROLES[role]?.ton || 'neutral'}>
                            {ROLES[role]?.libelle || role}
                        </Pill>
                    ))}
                </span>
            );
        },
    },
];

export function UserGroups() {
    const { isActiveGroup, currentGroup } = useSelector((state) => state.group);

    const donnees = isActiveGroup ? (currentGroup?.value ?? currentGroup) : [];
    const groupes = Array.isArray(donnees) ? donnees : [];

    const proprietaire = groupes.filter((groupe) => (groupe.accessType || []).includes("owner")).length;

    return (
        <PaneTable
            titre="Groupes"
            colonnes={COLONNES}
            donnees={groupes}
            triInitial={TRI_PAR_NOM}
            placeholder="Nom, adresse, type…"
            badges={proprietaire > 0 && (
                <Pill $tone="accent">
                    propriétaire de {proprietaire} groupe{proprietaire > 1 ? 's' : ''}
                </Pill>
            )}
            vide={{
                icone: 'bx-group',
                titre: "Aucun groupe",
                texte: "Ce compte n'appartient à aucun groupe Entra ID ou Exchange.",
            }}
        />
    );
}
